import { featureFlags } from "@/featureFlags";

export type NumericRange = { min: number; max: number };

/** Present finite number, or null when the metric is missing/invalid. */
export function parseCount(value: unknown): number | null {
  if (value === undefined || value === null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Same normalization as the recommendation count filter: invalid → 0. */
export function toCount(value: unknown): number {
  return parseCount(value) ?? 0;
}

/** Min/max of present numeric values. Missing/invalid entries are skipped. */
export function numericRange(values: unknown[]): NumericRange | null {
  let min = Infinity;
  let max = -Infinity;
  let found = false;
  for (const value of values) {
    const n = parseCount(value);
    if (n === null) continue;
    found = true;
    if (n < min) min = n;
    if (n > max) max = n;
  }
  return found ? { min, max } : null;
}

export type RecommendationMetricItem = {
  total_packets?: string;
  total_bytes?: string;
  connections_count?: string;
  src_frequency?: number;
  dst_frequency?: number;
};

export type RecommendationMetricType =
  | "connections_count"
  | "packet_count"
  | "byte_count"
  | "src_frequency"
  | "dst_frequency";

export function recommendationMetricRanges(items: RecommendationMetricItem[]): {
  packet_count: NumericRange | null;
  byte_count: NumericRange | null;
  connections_count: NumericRange | null;
  src_frequency: NumericRange | null;
  dst_frequency: NumericRange | null;
} {
  return {
    // gRPC-gateway omits zero scalars: missing means 0.
    packet_count: numericRange(
      items.map((item) => toCount(item.total_packets)),
    ),
    byte_count: numericRange(items.map((item) => toCount(item.total_bytes))),
    connections_count: numericRange(
      items.map((item) => toCount(item.connections_count)),
    ),
    src_frequency: numericRange(
      items.map((item) => toCount(item.src_frequency)),
    ),
    dst_frequency: numericRange(
      items.map((item) => toCount(item.dst_frequency)),
    ),
  };
}

export function recommendationMetricFilterLabels(t: (key: string) => string): {
  connections: string;
  packets: string;
  bytes: string;
  srcFrequency: string;
  dstFrequency: string;
} {
  return {
    connections: t("traffic_control.filter.connections_count"),
    packets: t("traffic_control.filter.packet_count"),
    bytes: t("traffic_control.filter.byte_count"),
    srcFrequency: t("traffic_control.filter.src_frequency"),
    dstFrequency: t("traffic_control.filter.dst_frequency"),
  };
}

export function packetConnectionFilterOptions(
  items: RecommendationMetricItem[],
  labels: {
    connections: string;
    packets: string;
    bytes: string;
    srcFrequency: string;
    dstFrequency: string;
  },
): Array<{
  value: RecommendationMetricType;
  label: string;
  min?: number;
  max?: number;
  unit?: "bytes";
}> {
  const ranges = recommendationMetricRanges(items);
  const options: Array<{
    value: RecommendationMetricType;
    label: string;
    min?: number;
    max?: number;
    unit?: "bytes";
  }> = [
    {
      value: "connections_count",
      label: labels.connections,
      ...(ranges.connections_count ?? {}),
    },
    {
      value: "packet_count",
      label: labels.packets,
      ...(ranges.packet_count ?? {}),
    },
    {
      value: "byte_count",
      label: labels.bytes,
      unit: "bytes",
      ...(ranges.byte_count ?? {}),
    },
    {
      value: "src_frequency",
      label: labels.srcFrequency,
      ...(ranges.src_frequency ?? {}),
    },
    {
      value: "dst_frequency",
      label: labels.dstFrequency,
      ...(ranges.dst_frequency ?? {}),
    },
  ];
  if (featureFlags.recommendationFrequencyFilters) return options;
  return options.filter(
    (opt) => opt.value !== "src_frequency" && opt.value !== "dst_frequency",
  );
}

/** Empty stays empty. Values outside [min, max] snap to the bound. */
export function clampRangeValue(
  raw: string,
  min?: number,
  max?: number,
): string {
  if (raw === "") return "";
  const n = Number(raw);
  if (!Number.isFinite(n)) return "";
  if (min !== undefined && n < min) return String(min);
  if (max !== undefined && n > max) return String(max);
  return raw;
}

export type MetricFilterRow = { type: string; from: string; to: string };

export function applyRecommendationMetricFilters(
  rows: MetricFilterRow[],
  handlers: {
    onSelectedPacketsRange: (from: string, to: string) => void;
    onSelectedBytesRange: (from: string, to: string) => void;
    onSelectedConnectionsRange: (from: string, to: string) => void;
    onSelectedSrcFrequencyRange: (from: string, to: string) => void;
    onSelectedDstFrequencyRange: (from: string, to: string) => void;
  },
) {
  const row = (type: RecommendationMetricType) =>
    rows.find((item) => item.type === type);
  const packets = row("packet_count");
  const bytes = row("byte_count");
  const connections = row("connections_count");
  const srcFrequency = row("src_frequency");
  const dstFrequency = row("dst_frequency");
  handlers.onSelectedPacketsRange(packets?.from ?? "", packets?.to ?? "");
  handlers.onSelectedBytesRange(bytes?.from ?? "", bytes?.to ?? "");
  handlers.onSelectedConnectionsRange(
    connections?.from ?? "",
    connections?.to ?? "",
  );
  handlers.onSelectedSrcFrequencyRange(
    srcFrequency?.from ?? "",
    srcFrequency?.to ?? "",
  );
  handlers.onSelectedDstFrequencyRange(
    dstFrequency?.from ?? "",
    dstFrequency?.to ?? "",
  );
}

export function clampMetricFilterRows<T extends MetricFilterRow>(
  rows: T[],
  options: Array<{ value: string; min?: number; max?: number }>,
): T[] {
  return rows.map((row) => {
    const opt = options.find((o) => o.value === row.type);
    return {
      ...row,
      from: clampRangeValue(row.from, opt?.min, opt?.max),
      to: clampRangeValue(row.to, opt?.min, opt?.max),
    };
  });
}

export function metricFilterRowsEqual(
  a: MetricFilterRow[],
  b: MetricFilterRow[],
): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (row, i) =>
      row.type === b[i].type && row.from === b[i].from && row.to === b[i].to,
  );
}

/**
 * Inclusive range check. When both bounds are set and inverted (from > to),
 * they are swapped so "between 100 and 10" still means [10, 100].
 */
export function inNumericRange(
  value: number,
  from: string,
  to: string,
): boolean {
  let fromN = from ? toCount(from) : null;
  let toN = to ? toCount(to) : null;
  if (fromN !== null && toN !== null && fromN > toN) {
    [fromN, toN] = [toN, fromN];
  }
  if (fromN !== null && value < fromN) return false;
  if (toN !== null && value > toN) return false;
  return true;
}

// Binary multiples with the same labels as formatBytes in the tables.
export const BYTE_UNITS: Array<{ label: string; factor: number }> = [
  { label: "B", factor: 1 },
  { label: "KB", factor: 1024 },
  { label: "MB", factor: 1024 ** 2 },
  { label: "GB", factor: 1024 ** 3 },
];

/** Largest unit in which max still reads as ≥ 10, so typical values stay whole numbers. */
export function defaultByteUnit(max?: number): number {
  if (max === undefined) return 1;
  const fit = [...BYTE_UNITS].reverse().find((u) => max >= u.factor * 10);
  return fit?.factor ?? 1;
}

/**
 * Keeps digits and, when allowed, one decimal separator. "," is read as "."
 * only where it is the locale's decimal separator; otherwise it's a thousands
 * separator and is dropped like the grouping spaces.
 */
export function sanitizeTypedNumber(
  text: string,
  allowDecimal: boolean,
  commaIsDecimal = true,
): string {
  const normalized = commaIsDecimal
    ? text.replace(/,/g, ".")
    : text.replace(/,/g, "");
  let out = "";
  let seenDot = false;
  for (const ch of normalized) {
    if (ch >= "0" && ch <= "9") out += ch;
    else if (ch === "." && allowDecimal && !seenDot) {
      seenDot = true;
      out += out === "" ? "0." : ".";
    }
  }
  return out;
}

/** "1234567.5" → "1 234 567,5" (NBSP groups, locale decimal separator). */
export function groupDigits(raw: string, decimalSep = "."): string {
  const [int, frac] = raw.split(".");
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, "\u00A0");
  return frac === undefined ? grouped : `${grouped}${decimalSep}${frac}`;
}

export type UnitRounding = "round" | "floor" | "ceil";

/**
 * Base value shown in the given unit: at most 2 decimals, no trailing zeros.
 * floor/ceil keep a rounded max/min inside the real range.
 */
export function toUnitText(
  value: number,
  factor: number,
  mode: UnitRounding = "round",
): string {
  if (factor === 1) return String(value);
  // nudge against float noise so exact values (1536 / 1024 = 1.5) don't drift a cent
  const nudge = mode === "floor" ? 1e-9 : mode === "ceil" ? -1e-9 : 0;
  return String(Math[mode]((value / factor) * 100 + nudge) / 100);
}

function isExactIn(value: number, factor: number): boolean {
  return fromUnitText(toUnitText(value, factor), factor) === String(value);
}

/**
 * Unit for a byte row: the preferred one (last picked, else the default for
 * max) unless it would show an applied value rounded — then the largest unit
 * that shows every value exactly (B always does).
 */
export function pickByteUnit(
  max: number | undefined,
  values: number[],
  preferred?: number,
): number {
  const first = preferred ?? defaultByteUnit(max);
  if (values.every((v) => isExactIn(v, first))) return first;
  const exact = [...BYTE_UNITS]
    .reverse()
    .find((u) => values.every((v) => isExactIn(v, u.factor)));
  return exact?.factor ?? 1;
}

/** Typed text in a unit → base value as a digit string ("" stays ""). */
export function fromUnitText(raw: string, factor: number): string {
  if (raw === "") return "";
  const n = Number(raw);
  if (!Number.isFinite(n)) return "";
  return String(Math.round(n * factor));
}
